import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/components/common/SafeIcon';
import { ncbService } from '@/services/ncb';
import { useAuthContext } from '@/components/auth/AuthProvider';

const { FiMail, FiLock, FiUser, FiAlertCircle, FiCheckCircle } = FiIcons;

export default function Register() {
  const [providers, setProviders] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const navigate = useNavigate();
  const { refreshSession } = useAuthContext(); // 🔧 修復:取得 refreshSession

  useEffect(() => {
    const checkAPI = async () => {
      try {
        const data = await ncbService.getProviders();
        setProviders(data.providers || {});
        setApiError('');
      } catch (err) {
        console.error('API 連線錯誤:', err);
        setApiError('無法連接到伺服器,請確認 API URL 設定');
        setProviders({ email: true, google: true });
      }
    };
    checkAPI();
  }, []);

  const handleEmailRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('密碼與確認密碼不符');
      return;
    }

    if (password.length < 8) {
      setError('密碼至少需要 8 個字元');
      return;
    }

    setLoading(true);

    try {
      await ncbService.register(email, password, name);
      
      // 🔧 修復:註冊成功後立即刷新 session
      await refreshSession();
      
      setTimeout(() => {
        navigate('/bar');
      }, 100);
    } catch (err) {
      console.error('註冊錯誤:', err);
      setError(err.message || '註冊失敗,請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = () => {
    const callbackURL = window.location.origin + '/auth/callback';
    window.location.href = ncbService.getGoogleAuthUrl(callbackURL);
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#0a0a0c] flex items-center justify-center p-6">
      <div className="grain-overlay" />
      <div className="vignette" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="text-center mb-10">
          <h1 className="text-3xl font-serif text-amber-400 tracking-widest mb-3">
            未眠酒吧
          </h1>
          <p className="text-gray-500 text-sm tracking-wider">
            Midnight Bar
          </p>
        </div>

        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8 shadow-2xl">
          <h2 className="text-xl text-white mb-6 font-light tracking-widest">
            註冊新帳號
          </h2>

          {apiError && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
              <div className="flex items-start space-x-3 mb-3">
                <SafeIcon icon={FiAlertCircle} className="text-red-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-red-300 text-sm font-medium mb-2">{apiError}</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center space-x-3">
              <SafeIcon icon={FiAlertCircle} className="text-red-400" />
              <span className="text-red-300 text-sm">{error}</span>
            </div>
          )}

          <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-start space-x-3">
            <SafeIcon icon={FiCheckCircle} className="text-amber-400 mt-0.5" />
            <div className="text-amber-200 text-xs leading-relaxed">
              <p className="font-medium mb-1">免費會員福利:</p>
              <ul className="list-disc list-inside space-y-1 text-amber-300/80">
                <li>每日 5 則訊息</li>
                <li>每日 1 次單張塔羅</li>
              </ul>
            </div>
          </div>

          {providers?.google && (
            <button
              onClick={handleGoogleSignUp}
              className="w-full mb-6 bg-white text-gray-800 py-3 rounded-xl font-medium tracking-widest hover:bg-gray-100 transition-colors flex items-center justify-center space-x-3"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>使用 Google 註冊</span>
            </button>
          )}

          {providers?.google && providers?.email && (
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-[#0a0a0c] px-4 text-gray-500">或</span>
              </div>
            </div>
          )}

          {providers?.email && (
            <form onSubmit={handleEmailRegister}>
              <div className="space-y-5">
                <div>
                  <label className="block text-gray-400 text-xs tracking-widest mb-2">
                    名字 (可選)
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="你的名字"
                      className="w-full bg-white/10 text-white pl-12 pr-4 py-3 rounded-xl border border-white/10 focus:border-amber-500/50 focus:outline-none transition-colors"
                    />
                  </div>
                  <p className="text-gray-500 text-[10px] mt-1 ml-1">
                    填寫後,AI 角色會直接稱呼你的名字 ✨
                  </p>
                </div>

                <div>
                  <label className="block text-gray-400 text-xs tracking-widest mb-2">
                    EMAIL
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full bg-white/10 text-white pl-12 pr-4 py-3 rounded-xl border border-white/10 focus:border-amber-500/50 focus:outline-none transition-colors"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-400 text-xs tracking-widest mb-2">
                    PASSWORD
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="至少 8 個字元"
                      className="w-full bg-white/10 text-white pl-12 pr-4 py-3 rounded-xl border border-white/10 focus:border-amber-500/50 focus:outline-none transition-colors"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-400 text-xs tracking-widest mb-2">
                    CONFIRM PASSWORD
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="再次輸入密碼"
                      className="w-full bg-white/10 text-white pl-12 pr-4 py-3 rounded-xl border border-white/10 focus:border-amber-500/50 focus:outline-none transition-colors"
                      required
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !!apiError}
                className="w-full mt-8 bg-amber-500 text-black py-3 rounded-xl font-medium tracking-widest hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '註冊中...' : '加入酒吧'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <span className="text-gray-500 text-sm">已有帳號? </span>
            <Link
              to="/login"
              className="text-amber-400 hover:text-amber-300 text-sm transition-colors"
            >
              立即登入
            </Link>
          </div>

          <p className="mt-6 text-gray-600 text-[10px] text-center leading-relaxed">
            註冊即表示同意
            <Link to="/terms" className="text-amber-500 hover:text-amber-400 mx-1">
              服務條款
            </Link>
            與
            <Link to="/privacy" className="text-amber-500 hover:text-amber-400 mx-1">
              隱私權政策
            </Link>
          </p>
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/"
            className="text-gray-500 hover:text-amber-400 text-xs tracking-widest transition-colors"
          >
            ← 返回首頁
          </Link>
        </div>
      </motion.div>
    </div>
  );
}